import  { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBackIosNew,
  ArrowForwardIos,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

/* ── Swiper ── */
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Keyboard, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';


/* ═══════════════════════════════════════════
   COLOR SYSTEM — Ocean Blue -> Teal
   ═══════════════════════════════════════════ */
const C = {
  b50: '#e6f9ff', b100: '#b3eeff', b200: '#66dcff',
  b500: '#03c0ff', b600: '#02a8e0', b700: '#0187b3', b900: '#014d66',
  t400: '#fda832', t500: '#fc8d05', t600: '#d97604',
  grad: 'linear-gradient(135deg, #03c0ff 0%, #fc8d05 100%)',
  gradSoft: 'linear-gradient(135deg, #03c0ff 0%, #fc8d05 100%)',
  gradBg: 'linear-gradient(135deg, #e6f9ff 0%, #fffbf0 40%, #f8fafc 70%, #fff7ed 100%)',
  glow: 'rgba(3,192,255,',
  glowT: 'rgba(252,141,5,',
  gradDark: 'linear-gradient(135deg, #011217 0%, #0f0801 100%)',
};

/* ═══════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════ */
const HeroBadge = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 22px',
  borderRadius: 50,
  background: 'rgba(255,255,255,0.75)',
  backdropFilter: 'blur(16px)',
  border: '1px solid rgba(37,99,235,0.12)',
  boxShadow: '0 2px 16px rgba(37,99,235,0.08), inset 0 1px 0 rgba(255,255,255,0.6)',
  marginBottom: 28,
});

const GradientBtn = styled(Button)({
  fontWeight: 700,
  fontSize: '1rem',
  textTransform: 'none',
  borderRadius: 14,
  padding: '15px 36px',
  color: '#fff',
  background: C.grad,
  backgroundSize: '200% auto',
  boxShadow: `0 8px 32px ${C.glow}0.25), 0 2px 8px ${C.glowT}0.15)`,
  transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)',
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, #1d4ed8 0%, #0e7490 50%, #0d9488 100%)',
    opacity: 0,
    transition: 'opacity 0.4s ease',
    zIndex: -1,
  },
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: `0 16px 48px ${C.glow}0.3), 0 4px 12px ${C.glowT}0.2)`,
    backgroundPosition: 'right center',
    '&::after': { opacity: 1 },
  },
});

const NavArrow = styled(IconButton)({
  width: 52,
  height: 52,
  background: 'rgba(255,255,255,0.8)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(37,99,235,0.1)',
  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  color: '#64748b',
  transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
  '&:hover': {
    background: '#fff',
    boxShadow: `0 8px 32px ${C.glow}0.15)`,
    color: C.b600,
    transform: 'scale(1.08)',
  },
});
/* ─── Browser Mockup Frame ─── */
const BrowserFrame = styled(Box)({
  borderRadius: 20,
  overflow: 'hidden',
  background: '#fff',
  border: '1px solid rgba(0,0,0,0.08)',
  boxShadow: '0 30px 90px rgba(37,99,235,0.12), 0 8px 32px rgba(0,0,0,0.06)',
  position: 'relative',
});

const BrowserBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '10px 16px',
  background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
  borderBottom: '1px solid rgba(0,0,0,0.06)',
});

const BrowserDot = styled(Box)(({ color }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: color,
}));

const BrowserUrlBar = styled(Box)({
  flex: 1,
  marginLeft: 12,
  padding: '5px 14px',
  borderRadius: 8,
  background: '#fff',
  border: '1px solid #e2e8f0',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
});

/* ═══════════════════════════════════════════
   SLIDES DATA — AIDA MODEL
   ═══════════════════════════════════════════ */
const SLIDES = [
  {
    id: 0,
    badge: { icon: '\u{1F680}', label: "Yeni Nesil Dernek Yönetim Sistemi" },
    title: 'Resmi Altyapılı ',
    highlight: 'Dernek Yönetim Platformu',
    desc: 'Üye yönetimi, aidat takibi, bağış sistemi, resmi yazışma ve web sitesi yönetimini tek platformda yönetin.',
    features: ['E-İmza destekli', 'Resmi yazışma altyapısı', 'Resmi yazışma altyapısı'],
    cta1: { text: 'Hemen Başlayın', to: '/basvuru' },
    cta2: { text: 'Demo İzleyin', to: '/demo' },
    img: '/assets/page_images/hero_slide1.webp',
    url: 'app.superdernek.com/dashboard',
    floatTop: { dot: '#22c55e', dotGlow: 'rgba(34,197,94,0.5)', label: 'Canlı Sistem' },
    floatBottom: { icon: '\u{1F310}', title: '1.200+ Dernek', sub: 'Aktif platform' },
    accent: '#0891b2', accentSoft: 'rgba(8,145,178,0.1)',
    highlightColor: '#0891b2',
    highlightGradient: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
  },
  {
    id: 1,
    badge: { icon: '🧩', label: 'Widget Tabanlı Sistem' },
    title: 'Web Sitenizi ',
    highlight: 'Özgürce Tasarlayın',
    desc: 'Tasarımı ekranda görerek yapın, sürprizlerle karşılaşmayın. Web sitenizi widget’lar ile şekillendirin, yönetim panelinde düzeni koruyun.',
    features: [
      'Web sitesi için esnek sayfa tasarımı',
      'Dashboard widget özelleştirme',
      'Mobil uyumlu yapı'
    ],
    cta1: { text: 'Widget Sistemini İncele', to: '/solutions' },
    cta2: { text: "Widget'ları Gör", to: '/solutions' },
    img: '/assets/page_images/hero_slide2.webp',
    url: 'app.superdernek.com/dashboard',
    floatTop: { dot: '#3b82f6', dotGlow: 'rgba(59,130,246,0.5)', label: '50.000+ Üye' },
    floatBottom: { icon: '\u{1F4CA}', title: '%98 Memnuniyet', sub: 'Kullanıcı skoru' },
    accent: '#8b5cf6', accentSoft: 'rgba(139,92,246,0.1)',
    highlightColor: '#8b5cf6',
    highlightGradient: 'linear-gradient(135deg, #f65c95ff 0%, #a78bfa 100%)',
  },
  {
    id: 2,
    badge: { icon: '\u{1F465}', label: 'Akıllı Üye Yönetimi' },
    title: 'Dernek Operasyonlarını ',
    highlight: 'Tek Panelde Yönetin',
    desc: 'Dernek yönetiminin tüm operasyonel yükünü tek panelde toplayın. Karmaşayı değil, kontrolü yönetin.',
    features: ['CRM & üye takibi', 'Toplu iletişim', 'Detaylı raporlama'],
    cta1: { text: 'Demo Talep Edin', to: '/basvuru' },
    cta2: { text: 'Özellikleri Keşfet', to: '/solutions' },
    img: '/assets/page_images/hero_slide3.webp',
    url: 'app.superdernek.com/members',
    floatTop: { dot: '#3b82f6', dotGlow: 'rgba(59,130,246,0.5)', label: '50.000+ Üye' },
    floatBottom: { icon: '\u{1F4CA}', title: '%98 Memnuniyet', sub: 'Kullanıcı skoru' },
    accent: '#10b981', accentSoft: 'rgba(16,185,129,0.1)',
    highlightColor: '#10b981',
    highlightGradient: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
  },
  {
    id: 3,
    badge: { icon: '\u{1F4B0}', label: 'Aidat & Bağış Yönetimi' },
    title: 'Tahsilatlarınızı ',
    highlight: 'Kolayca Yönetin',
    desc: 'Aidat ve bağış süreçlerini manuel takibin dışına çıkarın. Ödemeler düzenli, kayıtlar net olsun.',
    features: ['Online bağış sistemi', 'Otomatik hatırlatma', 'Ödeme entegrasyonu'],
    cta1: { text: 'Ücretsiz Başlayın', to: '/basvuru' },
    cta2: { text: 'Fiyatları İncele', to: '/ucretlendirme' },
    img: '/assets/page_images/hero_slide4.webp',
    url: 'app.superdernek.com/finance',
    floatTop: { dot: '#f59e0b', dotGlow: 'rgba(245,158,11,0.5)', label: 'Gelir +%40' },
    floatBottom: { icon: '\u{1F514}', title: 'Otomatik Takip', sub: 'Aidat hatırlatma' },
    accent: '#f97316', accentSoft: 'rgba(249,115,22,0.1)',
    highlightColor: '#f97316',
    highlightGradient: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
  },
];

const STATS = [
  { val: '1.200+', lbl: 'Aktif Dernek' },
  { val: '50.000+', lbl: 'Kayıtlı Üye' },
  { val: '%99.9', lbl: 'Uptime SLA' },
];

/* ═══════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════ */
const ModernHero = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const swiperRef = useRef(null);

  return (
    <Box
      sx={{
        paddingTop: 2,
        position: 'relative',
        minHeight: { xs: 'auto', },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: C.gradBg,
      }}
    >
      {/* ── Grid dots ── */}
      <Box sx={{
        position: 'absolute', inset: 0, opacity: 0.35,
        backgroundImage: 'radial-gradient(#94a3b8 0.8px, transparent 0.8px)',
        backgroundSize: '28px 28px', pointerEvents: 'none',
      }} />

      {/* ── Navigation Arrows ── */}
      {!isMobile && (
        <>
          <Box sx={{ position: 'absolute', left: 24, top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}>
            <NavArrow onClick={() => swiperRef.current?.slidePrev()}>
              <ArrowBackIosNew sx={{ fontSize: 18 }} />
            </NavArrow>
          </Box>
          <Box sx={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}>
            <NavArrow onClick={() => swiperRef.current?.slideNext()}>
              <ArrowForwardIos sx={{ fontSize: 18 }} />
            </NavArrow>
          </Box>
        </>
      )}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          zIndex: 2,
        }}
      >
        <Swiper
          modules={[Autoplay, Keyboard, EffectFade]}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          speed={800}
          loop
          // autoplay={{
          //   delay: AUTOPLAY_DELAY,
          //   disableOnInteraction: false,
          //   pauseOnMouseEnter: true,
          // }}
          autoplay={false}
          keyboard={{ enabled: true }}
          onSwiper={(swiper) => { swiperRef.current = swiper; }}
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
          onAutoplayTimeLeft={(_, __, percentage) => setProgress(1 - percentage)}
          style={{ width: '100%' }}
        >
          {SLIDES.map((s) => (
            <SwiperSlide key={s.id}>
              <Container maxWidth="xl" sx={{ overflow: 'hidden' }}>
                <Grid
                  container
                  spacing={{ xs: 4, md: 8 }}
                  alignItems="center"
                  sx={{ minHeight: { xs: 'auto', md: '63vh' } }}
                >
                  {/* ════ LEFT — Text Content ════ */}
                  <Grid item xs={12} md={6}>
                    <Box className="hero-text">
                      {/* Badge */}
                      <HeroBadge>
                        <Typography sx={{ fontSize: '0.75rem', lineHeight: 1 }}>
                          <DoubleArrowIcon fontSize='18' />
                        </Typography>
                        <Typography sx={{
                          fontSize: '0.8rem', fontWeight: 700, letterSpacing: 0.3,
                          background: C.gradDark,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}>
                          {s.badge.label}
                        </Typography>
                      </HeroBadge>

                      {/* Title */}
                      <Typography variant="h1" sx={{
                        fontSize: { xs: '2.4rem', sm: '3rem', md: '3.6rem' },
                        fontWeight: 900, lineHeight: 1.08, color: '#0f172a',
                        letterSpacing: '-0.02em', mb: 2.5, maxWidth: 620,
                      }}>
                        {s.title}
                        <br />
                        <Box component="span" sx={{
                          background: s.highlightGradient,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          display: 'inline',
                        }}>
                          {s.highlight}
                        </Box>
                      </Typography>

                      {/* Description */}
                      <Typography sx={{
                        fontSize: { xs: '1rem', md: '1.12rem' },
                        color: '#475569', lineHeight: 1.85, mb: 3, maxWidth: 520,
                        fontWeight: 500,
                      }}>
                        {s.desc}
                      </Typography>

                      {/* Mini features list */}
                      <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 2.5 }, flexWrap: 'wrap', mb: 4 }}>
                        {s.features.map((f, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                            <CheckIcon sx={{ fontSize: 16, color: C.t500 }} />
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>
                              {f}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* CTAs */}
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: { xs: 3, md: 5 } }}>
                        <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                          <GradientBtn endIcon={<ArrowForwardIcon />} onClick={() => navigate(s.cta1.to)}>
                            {s.cta1.text}
                          </GradientBtn>
                        </motion.div>
                        {/* <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                          <OutlineBtn startIcon={<PlayIcon />} onClick={() => navigate(s.cta2.to)}>
                            {s.cta2.text}
                          </OutlineBtn>
                        </motion.div> */}
                      </Box>

                      {/* Trust stats */}
                      <Box sx={{
                        display: 'flex', gap: { xs: 3, md: 5 }, flexWrap: 'wrap',
                        pt: 2.5, borderTop: '1px solid #e2e8f0',
                      }}>
                        {STATS.map((st, i) => (
                          <Box key={i}>
                            <Typography sx={{
                              fontWeight: 900, fontSize: '1.15rem',
                              background: C.gradDark,
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                            }}>
                              {st.val}
                            </Typography>
                            <Typography sx={{
                              fontSize: '0.72rem', color: '#64748b',
                              fontWeight: 500, letterSpacing: 0.5, mt: 0.2,
                            }}>
                              {st.lbl}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Grid>

                  {/* ════ RIGHT — Browser Mockup ════ */}
                  <Grid item xs={12} md={6}>
                    <Box className="hero-image" sx={{ position: 'relative', maxWidth: 620, mx: 'auto', py: { xs: 3, md: 2 } }}>

                      {/* ── Decorative rotated backdrop ── */}
                      <Box sx={{
                        position: 'absolute',
                        top: '8%', right: '-6%', bottom: '8%', left: '6%',
                        borderRadius: '32px',
                        background: s.accentSoft,
                        border: `1px solid ${s.accent}25`,
                        transform: 'rotate(3deg)',
                        zIndex: 0,
                        transition: 'background 0.6s ease, border-color 0.6s ease',
                        display: { xs: 'none', md: 'block' },
                      }} />


                      {/* Browser window — 3D perspective + accent glow */}
                      <Box
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => navigate(s.cta1.to)}
                        sx={{
                          background: s.gradient,
                          backgroundSize: '200% auto',
                          boxShadow: `0 8px 32px ${s.accent}40, 0 2px 8px ${s.accent}20`,
                          filter: `drop-shadow(0 24px 60px ${s.accent}28)`,
                          transform: { xs: 'none', md: 'perspective(1400px) rotateY(-5deg) rotateX(2deg)' },
                          transition: 'transform 0.6s cubic-bezier(0.4,0,0.2,1), filter 0.6s ease',
                          '&:hover': {
                             boxShadow: `0 16px 48px ${s.accent}50, 0 4px 12px ${s.accent}30`,
                            transform: { xs: 'none', md: 'perspective(1400px) rotateY(-1deg) rotateX(0deg)' },
                            filter: `drop-shadow(0 32px 80px ${s.accent}38)`,
                          },
                        }}>
                        <BrowserFrame>
                          <BrowserBar>
                            <BrowserDot color="#ff5f57" />
                            <BrowserDot color="#febc2e" />
                            <BrowserDot color="#28c840" />
                            <BrowserUrlBar>
                              <Box sx={{
                                width: 12, height: 12, borderRadius: 2,
                                background: C.grad, flexShrink: 0,
                              }} />
                              <Typography sx={{
                                fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500,
                                overflow: 'hidden', whiteSpace: 'nowrap',
                              }}>
                                {s.url}
                              </Typography>
                            </BrowserUrlBar>
                          </BrowserBar>
                          <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                            <img
                              src={s.img}
                              alt={s.highlight}
                              loading="eager"
                              style={{ width: '100%', height: 'auto', display: 'block' }}
                            />
                            <Box sx={{
                              position: 'absolute', bottom: 0, left: 0, right: 0, height: '30%',
                              background: 'linear-gradient(transparent, rgba(255,255,255,0.4))',
                              pointerEvents: 'none',
                            }} />
                          </Box>
                        </BrowserFrame>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Container>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      {/* ── Slide Tabs Navigation ── */}
      <Box sx={{
        position: 'relative', zIndex: 5,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'stretch',
        gap: { xs: 1, md: 1.5 },
        pb: 5, pt: 2, px: 2,
        flexWrap: 'wrap',
      }}>
        {SLIDES.map((sl, i) => (
          <Box
            key={i}
            onClick={() => swiperRef.current?.slideToLoop(i)}
            sx={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 0.5,
              px: { xs: 1.5, md: 2.5 },
              py: { xs: 1, md: 1.2 },
              borderRadius: '12px',
              cursor: 'pointer',
              minWidth: { xs: 60, md: 88 },
              background: i === activeIndex
                ? 'rgba(255,255,255,0.95)'
                : 'rgba(255,255,255,0.45)',
              border: i === activeIndex
                ? `1.5px solid ${C.b200}`
                : '1.5px solid transparent',
              boxShadow: i === activeIndex
                ? '0 4px 20px rgba(37,99,235,0.12), inset 0 1px 0 rgba(255,255,255,0.9)'
                : 'none',
              backdropFilter: 'blur(8px)',
              transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
              '&:hover': {
                background: 'rgba(255,255,255,0.8)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 24px rgba(37,99,235,0.1)',
              },
            }}
          >
            <Typography sx={{
              fontSize: '0.6rem', fontWeight: 800, letterSpacing: 1.5,
              ...(i === activeIndex
                ? { background: C.grad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }
                : { color: '#94a3b8' }),
            }}>
              {String(i + 1).padStart(2, '0')}
            </Typography>
            <Box sx={{
              width: i === activeIndex ? 28 : 16, height: 2, borderRadius: 1,
              background: i === activeIndex ? C.grad : '#e2e8f0',
              transition: 'all 0.4s ease',
            }} />
            {/* <Typography sx={{
              fontSize: '0.65rem', fontWeight: 600,
              color: i === activeIndex ? '#0f172a' : '#94a3b8',
              textAlign: 'center', lineHeight: 1.3,
              display: { xs: 'none', sm: 'block' },
              maxWidth: 80,
            }}>
              {sl.highlight}
            </Typography> */}
          </Box>
        ))}
      </Box>

      {/* ── Progress bar ── */}
      <Box sx={{
        position: 'absolute', bottom: 0, left: 0,
        width: '100%', height: 3, background: '#e2e8f0', zIndex: 5,
      }}>
        <Box sx={{
          height: '100%',
          width: `${progress * 100}%`,
          background: C.grad,
          borderRadius: 2,
          transition: 'width 0.1s linear',
        }} />
      </Box>
    </Box>
  );
};

export default ModernHero;
